import { Command } from 'cmdk';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Open on Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Reset query when closed
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  // Search with debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/leads?search=${encodeURIComponent(query)}&limit=10`);
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const handleSelect = (lead) => {
    setOpen(false);
    setQuery('');
    navigate(`/leads?highlight=${lead.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0 gap-0 bg-card border-white/10 max-w-2xl"
        data-testid="command-palette"
      >
        <Command className="bg-transparent" shouldFilter={false}>
          <div className="flex items-center border-b border-white/10 px-4">
            <Search className="w-4 h-4 text-muted-foreground mr-3 flex-shrink-0" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search leads, companies, contacts..."
              className="flex-1 bg-transparent py-4 text-sm text-foreground placeholder:text-muted-foreground outline-none border-0 focus:ring-0"
              data-testid="command-palette-input"
            />
            <kbd className="text-xs text-muted-foreground bg-white/5 px-2 py-1 rounded-sm">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-80 overflow-auto p-2">
            {loading && (
              <Command.Loading>
                <div className="p-4 text-sm text-muted-foreground text-center">
                  Searching...
                </div>
              </Command.Loading>
            )}

            {!loading && results.length === 0 && query.trim() && (
              <Command.Empty className="p-4 text-sm text-muted-foreground text-center">
                No leads found for &quot;{query}&quot;
              </Command.Empty>
            )}

            {!loading && results.length === 0 && !query.trim() && (
              <div className="p-4 text-xs text-muted-foreground text-center uppercase tracking-wider">
                Type to search leads
              </div>
            )}

            {results.length > 0 && (
              <Command.Group
                heading={
                  <span className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground/60">
                    Leads
                  </span>
                }
              >
                {results.map(lead => (
                  <Command.Item
                    key={lead.id}
                    value={`${lead.company_name} ${lead.contact_name} ${lead.id}`}
                    onSelect={() => handleSelect(lead)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-sm cursor-pointer hover:bg-white/5 aria-selected:bg-white/5 data-[selected=true]:bg-white/5"
                    data-testid={`cmd-result-${lead.id}`}
                  >
                    <div className="w-8 h-8 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-blue-400">
                        {lead.company_name?.[0]?.toUpperCase() ?? '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {lead.company_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {lead.contact_name}
                        {lead.stage ? ` · ${lead.stage}` : ''}
                      </p>
                    </div>
                    {lead.estimated_value != null && (
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        £{lead.estimated_value.toLocaleString()}
                      </span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className="border-t border-white/5 px-4 py-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              <kbd className="bg-white/5 px-1.5 py-0.5 rounded-sm">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="bg-white/5 px-1.5 py-0.5 rounded-sm">↵</kbd> open
            </span>
            <span>
              <kbd className="bg-white/5 px-1.5 py-0.5 rounded-sm">ESC</kbd> close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
